<template>
	<div
		class="d-flex width-full relative"
		:class="{
			'fixed top-0 bottom-0 left-0 right-0 bg-neutral-25 z-9 pt-10': isDialog,
			'pointer-events-none': disabled,
		}"
	>
		<PButton
			v-if="isDialog"
			type="text"
			color="neutral"
			icon="close"
			class="absolute right-0"
			@click="isDialog = false"
		></PButton>
		<div class="width-full" :class="{ 'px-4 pt-10': isDialog }">
			<div class="d-flex width-full align-start flex-column relative">
				<div ref="autocomplete" class="width-full d-flex">
					<InputText
						v-model="searchText"
						:label="label"
						:hide-error-box="hideErrorBox"
						:error="error"
						:size="size"
						:disabled="disabled"
						:placeholder="placeholder"
						:helper-text="helperTextMessage"
						@input="inputText"
						@focus="onFocus"
						@focusout="onFocusOut"
						@enter="onEnter"
					>
						<template #prefix-icon>
							<slot name="prefix">
								<img
									loading="lazy"
									width="16px"
									alt="search"
									src="@/components/passporterUi/icons/search.svg"
									class="mr-2"
								/>
							</slot>
						</template>
						<template #suffix-icon>
							<div class="d-flex">
								<slot name="suffix"></slot>
								<PButton
									v-if="searchText && showClearButton && !disabled"
									type="text"
									color="neutral"
									@click="clearButton"
								>
									<Icon icon="close" radius="20px" />
								</PButton>
								<PButton
									v-if="showCollapseIcon"
									type="text"
									color="neutral"
									@click="showResults = !showResults"
								>
									<Icon icon="chevron" :rotate="showResults ? 'down' : ''" radius="20px" />
								</PButton>
							</div>
						</template>
					</InputText>
				</div>
				<div ref="content" v-if="showResults" :style="floatingStyles" class="z-4">
					<div
						class="width-full absolute z-2 bottom-0 bg-white items-list px-5 py-3"
						:class="{
							'shadow-lg border-xs border-neutral-50 rounded-2': !isDialog,
						}"
					>
						<slot />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed } from 'vue'
	import {
		autoUpdate,
		flip,
		inline,
		offset,
		shift,
		size as floatingSize,
		useFloating,
	} from '@floating-ui/vue'
	const { t } = useI18n()

	interface Props {
		isXS: boolean
		label?: string
		hideErrorBox?: boolean
		error?: string
		placeholder?: string
		keepSelected?: boolean
		size?: 's' | 'm' | 'l'
		showCollapseIcon?: boolean
		showClearButton?: boolean
		disabled?: boolean
		helperText?: string
		minLength?: number
	}

	const props = withDefaults(defineProps<Props>(), {
		label: undefined,
		error: '',
		placeholder: '',
		size: 'm',
		helperText: '',
		minLength: 3,
	})
	const emit = defineEmits(['browse', 'clear', 'focusout', 'enter', 'focus', 'clearClicked'])

	let debounceTimeout: any = null

	const searchText = defineModel<string>()
	const autocomplete = ref<HTMLElement>()
	const content = ref<HTMLElement>()
	const showResults = ref<boolean>(false)
	const isDialog = ref<boolean>(false)

	const helperTextMessage = computed(() => {
		const len = searchText.value?.length ?? 0
		return len === 0 || len >= props.minLength ? props.helperText : t('Search_Common_Emptycase')
	})

	const inputText = (inputEvent: InputEvent) => {
		if (props.isXS) isDialog.value = true
		if (debounceTimeout) {
			clearTimeout(debounceTimeout)
		}
		const text = (inputEvent.target as HTMLInputElement)?.value ?? ''
		if (text.length < props.minLength) {
			showResults.value = false
			return emit('clear')
		}
		showResults.value = true
		debounceTimeout = setTimeout(() => {
			emit('browse', text)
		}, 400)
	}

	const onFocus = () => {
		if (props.isXS) isDialog.value = true
		showResults.value = props.minLength === 0
		emit('focus')
	}

	const onFocusOut = () => {
		setTimeout(() => {
			isDialog.value = false
			if (!props.keepSelected) {
				searchText.value = undefined
			}
			showResults.value = false
			emit('clear')

			emit('focusout')
		}, 300)
	}

	const onEnter = () => {
		showResults.value = false
		isDialog.value = false
		emit('enter')
	}

	const clearButton = () => {
		emit('clearClicked')
		searchText.value = ''
	}

	const closeDialog = () => {
		isDialog.value = false
	}

	const closeResults = () => {
		showResults.value = false
	}

	defineExpose({ closeDialog, closeResults })

	const { floatingStyles } = useFloating(autocomplete, content, {
		placement: 'bottom-start',
		strategy: 'fixed',
		middleware: [
			flip(),
			shift(),
			offset(props.hideErrorBox ? 0 : -26),
			inline(),
			floatingSize({
				apply: ({ rects, elements }) => {
					Object.assign(elements.floating.style, {
						width: `${rects.reference.width}px`,
					})
				},
			}),
		],
		whileElementsMounted: autoUpdate,
	})
</script>

<style scoped lang="scss">
	.items-list {
		transform: translateY(100%);
		overflow-y: auto;
	}
</style>
